// File: frontend/src/pages/exams/EnterResults.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/axios';

const EnterResults = () => {
  const { examId } = useParams();

  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState(examId || '');
  const [selectedExam, setSelectedExam] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(null);

  const [students, setStudents] = useState([]);
  const [marksMap, setMarksMap] = useState({}); // { studentId: { marks_obtained, remarks } }

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  /**
   * Helper to calculate letter grade dynamically
   */
  const calculateGrade = (marksObtained, totalMarks = 100) => {
    if (marksObtained === '' || marksObtained === undefined || marksObtained === null) return '-';
    const obtained = parseFloat(marksObtained);
    const total = parseFloat(totalMarks || 100);
    if (isNaN(obtained) || total <= 0) return '-';

    const percentage = (obtained / total) * 100;

    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  /**
   * Fetch all exams for dropdown selection
   */
  const fetchInitialExams = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/exams');
      if (response.data?.success) {
        const list = response.data.data || [];
        setExams(list);
        if (examId) {
          setSelectedExamId(examId);
        } else if (list.length > 0) {
          setSelectedExamId(String(list[0].id));
        }
      }
    } catch (err) {
      console.error('Error fetching exams:', err);
    }
  }, [examId]);

  useEffect(() => {
    fetchInitialExams();
  }, [fetchInitialExams]);

  /**
   * Fetch active subjects for current class
   */
  const fetchSubjects = useCallback(async (classId) => {
    try {
      const response = await api.get(`/api/v1/exams/subjects?class_id=${classId}`);
      if (response.data?.success) {
        const list = response.data.data || [];
        setSubjects(list);
        if (list.length > 0) {
          setSelectedSubjectId(String(list[0].id));
          setSelectedSubject(list[0]);
        } else {
          setSelectedSubjectId('');
          setSelectedSubject(null);
        }
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  }, []);

  /**
   * Fetch enrolled students in the target class
   */
  const fetchStudents = useCallback(async (classId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/students', {
        params: { class_id: classId, limit: 100 }
      });
      if (response.data?.success) {
        const list = response.data.data || [];
        setStudents(list);

        // Initialize marks map state
        const initialMap = {};
        list.forEach((st) => {
          initialMap[st.id] = { marks_obtained: '', remarks: '' };
        });
        setMarksMap(initialMap);
      }
    } catch (err) {
      console.error('Error fetching students for exam:', err);
      setError('Failed to load students for this class.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handle selecting an examination
   */
  const handleExamChange = useCallback((examIdVal) => {
    setSelectedExamId(examIdVal);
    if (!examIdVal) {
      setSelectedExam(null);
      setSubjects([]);
      setSelectedSubjectId('');
      setSelectedSubject(null);
      setStudents([]);
      return;
    }
    const examObj = exams.find((e) => String(e.id) === String(examIdVal));
    if (examObj) {
      setSelectedExam(examObj);
      if (examObj.class_id) {
        fetchSubjects(examObj.class_id);
        fetchStudents(examObj.class_id);
      }
    }
  }, [exams, fetchSubjects, fetchStudents]);

  useEffect(() => {
    if (selectedExamId && exams.length > 0) {
      const examObj = exams.find((e) => String(e.id) === String(selectedExamId));
      if (examObj) {
        setSelectedExam(examObj);
        if (examObj.class_id) {
          fetchSubjects(examObj.class_id);
          fetchStudents(examObj.class_id);
        }
      }
    }
  }, [selectedExamId, exams, fetchSubjects, fetchStudents]);

  useEffect(() => {
    if (selectedSubjectId && subjects.length > 0) {
      const subObj = subjects.find((s) => String(s.id) === String(selectedSubjectId));
      setSelectedSubject(subObj || null);
    }
  }, [selectedSubjectId, subjects]);

  /**
   * Handle changes to a student's marks input
   */
  const handleMarksChange = (studentId, value) => {
    setMarksMap((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        marks_obtained: value
      }
    }));
  };

  /**
   * Handle changes to a student's remarks input
   */
  const handleRemarksChange = (studentId, value) => {
    setMarksMap((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks: value
      }
    }));
  };

  /**
   * Submit marks for all students in bulk
   */
  const handleSubmitAll = async (e) => {
    e.preventDefault();
    if (!selectedExamId || !selectedSubjectId) {
      setError('Please select both an examination and a subject.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMsg(null);

      const resultsArray = students
        .map((st) => {
          const entry = marksMap[st.id] || {};
          return {
            student_id: st.id,
            exam_id: selectedExamId,
            subject_id: selectedSubjectId,
            marks_obtained: entry.marks_obtained !== '' ? parseFloat(entry.marks_obtained) : 0,
            total_marks: selectedSubject?.total_marks || 100,
            remarks: entry.remarks || ''
          };
        })
        .filter((item) => item.student_id);

      const response = await api.post('/api/v1/exams/results', {
        exam_id: selectedExamId,
        subject_id: selectedSubjectId,
        results: resultsArray
      });

      if (response.data?.success) {
        setSuccessMsg(`Successfully submitted results for ${resultsArray.length} students!`);
      } else {
        setError(response.data?.message || 'Failed to submit results.');
      }
    } catch (err) {
      console.error('Error submitting exam results:', err);
      setError(err.response?.data?.message || err.message || 'Failed to submit exam results.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navigation Bar */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/exams" className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Enter Exam Marks</h1>
              <p className="text-xs text-slate-500">Record subject marks with auto-calculated grades</p>
            </div>
          </div>

          {selectedExamId && (
            <Link
              to={`/exams/results/${selectedExamId}`}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2 rounded-xl text-sm transition"
            >
              View Exam Results Sheet
            </Link>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Dropdowns Selection Bar */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Select Examination *</label>
            <select
              value={selectedExamId}
              onChange={(e) => handleExamChange(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Choose Examination --</option>
              {exams.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name} ({ex.class_name || `Class ${ex.class_id}`}) - {ex.term}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Select Subject *</label>
            <select
              value={selectedSubjectId}
              onChange={(e) => setSelectedSubjectId(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Choose Subject --</option>
              {subjects.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name} ({sub.code}) - Total Marks: {sub.total_marks}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {successMsg && (
          <div className="p-4 bg-emerald-50 border-l-4 border-emerald-500 text-emerald-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg(null)} className="font-bold text-emerald-600">✕</button>
          </div>
        )}

        {/* Marks Entry Table Card */}
        {selectedExamId && selectedSubjectId && (
          <form onSubmit={handleSubmitAll} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            
            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="font-bold text-slate-900 text-base">
                  {selectedSubject?.name || 'Subject'} Marks Entry Sheet
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Class: <span className="font-semibold text-slate-700">{selectedExam?.class_name || `Class ${selectedExam?.class_id}`}</span> • Total Subject Marks: <span className="font-bold text-primary">{selectedSubject?.total_marks || 100}</span>
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting || students.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow-md transition disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? 'Submitting Marks...' : 'Submit All Results'}
              </button>
            </div>

            {loading ? (
              <div className="p-12 text-center text-slate-400 font-medium">Loading enrolled students...</div>
            ) : students.length === 0 ? (
              <div className="p-12 text-center text-slate-400 font-medium">
                No students enrolled in this class.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs font-sans">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                      <th className="py-3.5 px-6">Roll No</th>
                      <th className="py-3.5 px-6">Student Name</th>
                      <th className="py-3.5 px-4 w-36">Marks Obtained</th>
                      <th className="py-3.5 px-4 text-center">Calculated Grade</th>
                      <th className="py-3.5 px-6">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                    {students.map((st) => {
                      const entry = marksMap[st.id] || { marks_obtained: '', remarks: '' };
                      const grade = calculateGrade(entry.marks_obtained, selectedSubject?.total_marks);

                      return (
                        <tr key={st.id} className="hover:bg-slate-50/80 transition">
                          <td className="py-3 px-6 font-mono font-bold text-slate-800">
                            {st.roll_no || st.student_id || '-'}
                          </td>
                          <td className="py-3 px-6 font-bold text-slate-900 text-sm">
                            {st.student_name}
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max={selectedSubject?.total_marks || 100}
                              placeholder="0"
                              value={entry.marks_obtained}
                              onChange={(e) => handleMarksChange(st.id, e.target.value)}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white"
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-block px-3 py-1 rounded-lg text-xs font-extrabold ${
                              grade === 'A+' || grade === 'A' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                              grade === 'B' || grade === 'C' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                              grade === 'D' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                              grade === 'F' ? 'bg-red-100 text-red-800 border border-red-300' :
                              'bg-slate-100 text-slate-400'
                            }`}>
                              {grade}
                            </span>
                          </td>
                          <td className="py-3 px-6">
                            <input
                              type="text"
                              placeholder="Optional remarks..."
                              value={entry.remarks}
                              onChange={(e) => handleRemarksChange(st.id, e.target.value)}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
              <button
                type="submit"
                disabled={submitting || students.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow-md transition disabled:opacity-50"
              >
                {submitting ? 'Submitting Marks...' : 'Submit All Results'}
              </button>
            </div>

          </form>
        )}

      </main>
    </div>
  );
};

export default EnterResults;
